import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ElementRef,
    AfterViewInit,
    Inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import {
    ApplicationFacade,
    CustomerFacade,
    MediumFacade,
    MediumGroup,
    MediumGroupType,
    RelatedCardFacade,
    TravelCardType
} from '@ridango/components';
import { AllowedCardTypes, AppStartupService, BaseComponent, RelatedCard } from '@ridango/core';
import Env from '@ridango/core/env';
import { flatten } from 'lodash';

import { Observable, fromEvent, of, iif, merge } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';

import { TravelCardInput } from '../purchase-medium.model';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { WidgetsService } from '../../../../widgets.service';
import { Keyboard } from '@capacitor/keyboard';

@Component({
    selector: 'rwidget-medium-selector',
    templateUrl: './medium-selector.component.html'
})
export class MediumSelectorComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() locale;
    @Input() step = 0;
    @Input() isEmpty = false;
    @Input() allowOnlyAddedCards = false;

    @Output() mediumSelected = new EventEmitter<TravelCardInput>();
    @ViewChild('mediumInput') mediumInput: ElementRef;

    @ViewChild('mediumInputContainer', { read: ElementRef })
    mediumInputContainer!: ElementRef<HTMLElement>;

    @ViewChild(MatAutocompleteTrigger, { static: false })
    autocompleteTrigger!: MatAutocompleteTrigger;

    label = 'UTN_CARD';
    inputType = 'text';

    mediumGroups$: Observable<MediumGroup[]> = this.mediumFacade.availableMediumGroups$.pipe(
        mergeMap((groups) =>
            iif(
                () => this.environment.PURCHASE.MERGE_MEDIUM_TRAVELCARDS,
                of(this.mergeGroupsByType(groups)),
                of(groups)
            )
        )
    );
    selectedMediumGroup$: Observable<MediumGroup> = this.mediumFacade.selectedMediumGroup$;
    assetsPath$: Observable<string> = this.applicationFacade.assetsPath$;
    allCards$: Observable<any> = this.relatedCardFacade.typeTravelcardCards$;

    selectedMediumGroup: MediumGroup;
    allCards: RelatedCard[] = [];
    visibleCards$: Observable<RelatedCard[]>;
    selectMediumForm: FormGroup;
    allowedCardTypes: AllowedCardTypes[] = [];
    selectedCardType: AllowedCardTypes;

    mediumGroupEnum = MediumGroupType;
    mediumAutocompleteDisabled = true;

    constructor(
        @Inject('ENV') public environment: Env,
        private appConfig: AppStartupService,
        private mediumFacade: MediumFacade,
        private applicationFacade: ApplicationFacade,
        private relatedCardFacade: RelatedCardFacade,
        private customerFacade: CustomerFacade,
        private widgetsService: WidgetsService
    ) {
        super();
    }

    ngOnInit() {
        this.selectMediumForm = new FormGroup({
            medium: new FormControl({ value: '', disabled: true })
        });

        this.visibleCards$ = this.selectMediumForm.controls.medium.valueChanges.pipe(
            startWith(''),
            switchMap(() => this.sortAutocompleteValues())
        );

        this.mediumGroups$.pipe(takeUntil(this.componentDestroyed$)).subscribe((groups) => {
            if (groups) {
                this.mediumTypeSelected(groups[0]);
            }
        });

        this.selectedMediumGroup$.pipe(takeUntil(this.componentDestroyed$)).subscribe((medium) => {
            if (medium) {
                this.selectedMediumGroup = medium;
                this.selectMediumForm.controls.medium.markAsUntouched();
                if (medium.travelcardTypes.includes(TravelCardType.Email)) {
                    this.setEmailType();
                } else {
                    this.setCardType(medium);
                }
            }
        });

        this.allCards$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cards) => {
            if (cards.length) {
                this.allCards = cards;

                if (this.selectedMediumGroup && this.selectedMediumGroup.type === MediumGroupType.Travelcard) {
                    this.visibleCards$ = this.sortAutocompleteValues();
                } else {
                    this.visibleCards$ = of([]);
                }
            }
        });
    }

    ngAfterViewInit() {
        const inputEvents$ = merge(
            fromEvent(this.mediumInput?.nativeElement, 'keyup'),
            fromEvent(this.mediumInput?.nativeElement, 'paste'),
            fromEvent(this.mediumInput?.nativeElement, 'input')
        );

        inputEvents$
            .pipe(
                filter(Boolean),
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    this.setSelectedMedium();
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();

        this.watchMediumInputSuggestionPopup();
    }

    async onToggleAutocomplete(event: MouseEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        if (!this.autocompleteTrigger) {
            return;
        }

        if (this.autocompleteTrigger.panelOpen) {
            this.autocompleteTrigger.closePanel();
            this.mediumAutocompleteDisabled = true;
        } else {
            this.mediumAutocompleteDisabled = false;
            this.autocompleteTrigger.openPanel();
        }

        if (this.mediumInput) {
            this.mediumInput.nativeElement.blur();
        }

        await Keyboard.hide().catch(() => undefined);
    }

    onAutocompleteClosed(): void {
        this.mediumAutocompleteDisabled = true;

        if (this.mediumInput) {
            this.mediumInput.nativeElement.blur();
        }

        Keyboard.hide().catch(() => undefined);
    }

    private watchMediumInputSuggestionPopup() {
        this.widgetsService.scrollStarted$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.autocompleteTrigger?.updatePosition();
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    mediumTypeSelected(mediumGroup) {
        this.mediumFacade.setSelectedMediumGroup(mediumGroup);
        this.selectedMediumGroup = mediumGroup;
        if (!mediumGroup?.travelcardTypes?.includes(TravelCardType.Email)) {
            this.selectMediumForm.controls.medium.setValue('');
        }
        this.selectMediumForm.controls.medium.enable();
        this.selectMediumForm.controls.medium.markAsPristine();
    }

    showMediumName(medium: RelatedCard) {
        return medium.name ? medium.name : medium.card_nr;
    }

    sortAutocompleteValues() {
        if (!this.selectedMediumGroup) {
            return of([]);
        }

        if (this.environment.PURCHASE.MERGE_MEDIUM_TRAVELCARDS) {
            return this.mediumFacade.availableMediumGroups$.pipe(
                map((v) =>
                    flatten(v.filter((g) => g.type === MediumGroupType.Travelcard).map((g) => g.travelcardTypes))
                ),
                map((travelCardTypes) => this.allCards.filter((c) => (travelCardTypes as any).includes(c.card_type)))
            );
        }

        return of(
            this.allCards.filter((card) =>
                this.selectedMediumGroup.travelcardTypes.find((type) => type === card.card_type)
            )
        );

        // In case we need to sort by user input:

        // const filterValue = searchStr.toLowerCase();
        // return this.allCards.filter(card =>
        //     this.selectedMediumGroup.travelcardTypes.find(type => type === card.card_type) && (
        //         card.name.toLowerCase().includes(filterValue) || card.card_nr.toLowerCase().includes(filterValue)
        // ));
    }

    setSelectedMedium() {
        this.mediumSelected.emit({
            type: this.selectedMediumGroup.type === 'Email' ? 'Email' : this.selectedMediumGroup.travelcardTypes[0],
            value: this.selectMediumForm.controls.medium.value,
            isValid: this.selectMediumForm.controls.medium.valid
        });
    }

    setEmailType() {
        this.label = 'LOCAL_LABEL_EMAIL';
        this.inputType = 'email';
        this.selectMediumForm.controls.medium.setValidators([Validators.required, Validators.email]);
        this.customerFacade.profile$.pipe(take(1)).subscribe((v) => {
            if (v?.email) {
                this.selectMediumForm.controls.medium.setValue(v.email);
                this.setSelectedMedium();
            }
        });
    }

    setCardType(medium) {
        this.label = 'UTN_CARD';
        this.selectMediumForm.controls.medium.setValidators([Validators.required, Validators.pattern('[0-9]*')]);

        const cardRegionProperty = this.appConfig.getPropertyByCode('WEB_ALLOWED_CARDS');
        if (cardRegionProperty) {
            const regionCardTypes = JSON.parse(cardRegionProperty.value);
            regionCardTypes.forEach((t) => {
                if (medium.travelcardTypes.includes(t.type)) {
                    this.selectMediumForm.controls.medium.setValidators([
                        Validators.required,
                        Validators.pattern('[0-9]*'),
                        Validators.minLength(t.cardNumberLength),
                        Validators.maxLength(t.cardNumberLength)
                    ]);
                    this.selectedMediumGroup.type = MediumGroupType.Travelcard;
                    this.label = t.translationTag;
                }
            });
        } else {
            if (medium && medium.display === 'MEDIUM_PERSON_ID') {
                this.label = 'MEDIUM_PERSON_ID';
            }
        }
    }

    private mergeGroupsByType(groups: MediumGroup[], type = MediumGroupType.Travelcard): MediumGroup[] {
        const filtered = groups.filter((g) => g.type === type);
        const excluded = groups.filter((g) => g.type !== type);
        return filtered.length ? excluded.concat(filtered[0]) : groups;
    }
}
